export { createBellSocket, parsePush, reconnectDelay, } from "./bell-socket.js";
import { messageFromResponseBody, normalizeBaseUrl, readResponseBody, withRequestTimeout, } from "./http.js";
export class NotificationClientError extends Error {
    status;
    body;
    constructor(status, body, message) {
        super(message);
        this.status = status;
        this.body = body;
        this.name = "NotificationClientError";
    }
}
export function createNotificationBrowserClient(options = {}) {
    const baseUrl = normalizeBaseUrl(options.baseUrl ?? "/api");
    const fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init));
    const timeoutMs = options.timeoutMs ?? 5000;
    // Generic in the response shape, not pinned to NotificationItem[]: the
    // unread-count route answers a scalar object. Generifying keeps every route
    // on the one helper that owns the timeout, the headers and `credentials` —
    // a second hand-rolled fetch is how the ws-ticket mint lost its
    // `x-csrf-token` and 403'd on every handshake (see useBellSocket.ts).
    async function request(path, init = {}) {
        return withRequestTimeout(timeoutMs, async (signal) => {
            const requestInit = {
                ...init,
                headers: buildHeaders(options, init.method ?? "GET", init.body !== undefined),
            };
            if (signal)
                requestInit.signal = signal;
            if (options.useCookies)
                requestInit.credentials = "include";
            const response = await fetchImpl(`${baseUrl}${path}`, requestInit);
            if (!response.ok) {
                throw await notificationClientErrorFromResponse(response);
            }
            return (await response.json());
        }, () => new NotificationClientError(0, null, "notification request timed out"));
    }
    return {
        list(params = {}) {
            return request(notificationListPath(params));
        },
        listPage(params = {}) {
            const path = notificationListPath(params);
            return withRequestTimeout(timeoutMs, async (signal) => {
                const init = { headers: buildHeaders(options, "GET") };
                if (signal)
                    init.signal = signal;
                if (options.useCookies)
                    init.credentials = "include";
                const response = await fetchImpl(`${baseUrl}${path}`, init);
                if (!response.ok) {
                    throw await notificationClientErrorFromResponse(response);
                }
                return {
                    items: (await response.json()),
                    nextCursor: response.headers.get("x-next-cursor"),
                };
            }, () => new NotificationClientError(0, null, "notification request timed out"));
        },
        unreadCount() {
            return request("/v1/notifications/unread-count");
        },
        get(id) {
            return request(`/v1/notifications/${encodeURIComponent(id)}`);
        },
        markRead(id) {
            return request(`/v1/notifications/${encodeURIComponent(id)}/read`, { method: "POST" });
        },
        markUnread(id) {
            return request(`/v1/notifications/${encodeURIComponent(id)}/unread`, { method: "POST" });
        },
        acknowledge(id) {
            return request(`/v1/notifications/${encodeURIComponent(id)}/acknowledge`, { method: "POST" });
        },
        markAllRead(renderedAt) {
            return request("/v1/notifications/read-all", {
                method: "POST",
                // Omitted rather than sent as null when the caller has no timestamp:
                // the server treats a non-string as now(), so both spellings behave
                // alike, and an absent key keeps the body valid against the spec's
                // `additionalProperties: false`.
                body: JSON.stringify(renderedAt === undefined ? {} : { renderedAt }),
            });
        },
        listPreferences() {
            return request("/v1/notifications/preferences");
        },
        setPreference(typeName, channel, enabled) {
            return request(`/v1/notifications/preferences/${encodeURIComponent(typeName)}`, {
                method: "PUT",
                body: JSON.stringify({ channel, enabled }),
            });
        },
    };
}
function notificationListPath(params) {
    const query = new URLSearchParams();
    if (params.limit !== undefined)
        query.set("limit", String(params.limit));
    if (params.cursor)
        query.set("cursor", params.cursor);
    if (params.unread !== undefined)
        query.set("unread", String(params.unread));
    if (params.moduleId)
        query.set("moduleId", params.moduleId);
    if (params.class)
        query.set("class", params.class);
    const suffix = query.toString();
    return suffix ? `/v1/notifications?${suffix}` : "/v1/notifications";
}
function buildHeaders(options, method, hasBody = false) {
    const headers = new Headers({ accept: "application/json" });
    const token = options.getAccessToken?.();
    if (token)
        headers.set("authorization", `Bearer ${token}`);
    // Without this the body arrives unparsed and the watermark is silently
    // dropped — the server reads `renderedAt` off a parsed object and falls back
    // to now(), which is exactly the bug sending a body is meant to fix.
    if (hasBody)
        headers.set("content-type", "application/json");
    if (!["GET", "HEAD"].includes(method.toUpperCase())) {
        const csrf = options.getCsrfToken?.();
        if (csrf)
            headers.set("x-csrf-token", csrf);
    }
    return headers;
}
async function notificationClientErrorFromResponse(response) {
    const body = await readResponseBody(response);
    return new NotificationClientError(response.status, body, errorMessage(response.status, body));
}
function errorMessage(status, body) {
    const responseMessage = messageFromResponseBody(body);
    if (responseMessage !== null)
        return responseMessage;
    return `notification request failed with ${status}`;
}
//# sourceMappingURL=browser.js.map